import { createMongoAbility } from '@casl/ability';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';

import { abilityOverrideAtom } from '#state/atoms/ability-override-atom.js';
import { RouteAuthorizationError } from '#utils/route-authorization.js';

// The purpose of this layout route is to block child routes from rendering until the user has been authorized to view
// them. It exists as a _child_ of the root layout because we would like the nav bar and other global UI elements to
// remain visible even when the authorization gate is preventing the current route from rendering.

export const Route = createFileRoute('/_authorization-gate')({
	beforeLoad: async ({ cause, matches, context: { queryClient, trpc } }) => {
		if (cause === 'preload') {
			return;
		}

		// This `beforeLoad` method is called for every route change. We use this as an opportunity to perform authorization
		// checks on every route navigation. To do this, we consult the `authorize` static data option of the every matched
		// route.
		// Also see `ability-context-provider.tsx`, which duplicates much of this logic but works within the React tree.

		// If none of the matched routes have an `authorize` static data option, we don't need to do anything.
		if (matches.every((match) => !match.staticData.authorize)) {
			return;
		}

		const currentUser = await queryClient.ensureQueryData(trpc.user.getCurrent.queryOptions());
		const abilityWithoutOverride = createMongoAbility(currentUser.auth.rules);

		const abilityOverride = getDefaultStore().get(abilityOverrideAtom);
		const abilityOverrideAbility = abilityOverride.rules ? createMongoAbility(abilityOverride.rules) : undefined;

		const abilityWithOverride = abilityOverrideAbility ?? abilityWithoutOverride;

		for (const match of matches) {
			if (!match.staticData.authorize) {
				continue;
			}

			const isAuthorized = match.staticData.authorize(abilityWithOverride);
			if (!isAuthorized) {
				throw new RouteAuthorizationError();
			}
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	return <Outlet />;
}
